import type {
  SuggestLocation,
  SuggestObject,
} from '@yandex-delivery/api/model/suggest'
import {Loader, Modal} from '@yandex-delivery/blocks'
import {isUndefined} from '@yandex-delivery/utils'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {i18n} from 'src/shared/config'
import type {PickupStation} from 'src/shared/types'

import {Filter, INITIAL_VAlUES} from './consts'
import {FiltersSidePanel} from './filters-side-panel/FiltersSidePanel'
import {Map as MapComponent} from './map/Map'
import * as classes from './PointsByCityModal.classes'
import {PointsSidePanel} from './points-side-panel/PointsSidePanel'
import type {FiltersValues, Props} from './types'
import {getFilterFunction, isFilterValues} from './utils'
import {getPointCoordinates} from './utils/get-point-coordinates'

export function PointsByCityModal(props: Props) {
  const {
    isOpen,
    isPending,
    pointType,
    points,
    onClose,
    onCitySelect,
    onPointSelect,
    selectedPoint: selectedPointProp,
    selectedPointCity,
  } = props
  const [address, setAddress] = useState<SuggestObject>()
  const [center, setCenter] = useState<SuggestLocation>()
  const [zoom, setZoom] = useState<number>()
  const [selectedFilters, setSelectedFilters] = useState<FiltersValues>()
  const [isFiltersPanelVisible, setIsFiltersPanelVisible] =
    useState<boolean>(false)
  const [selectedPoint, setSelectedPoint] = useState<PickupStation>()

  useEffect(() => {
    setSelectedPoint(selectedPointProp)
    setZoom(18)
    setCenter(getPointCoordinates(selectedPointProp?.position))
  }, [selectedPointProp])

  const filteredPoints = useMemo(() => {
    if (isUndefined(selectedFilters)) {
      return points
    }

    const filterFunction = getFilterFunction(selectedFilters) as (
      value: PickupStation,
    ) => boolean

    return points.filter(({value}) => filterFunction(value))
  }, [points, selectedFilters])

  const selectedFiltersAmountHandler = useMemo(() => {
    if (isUndefined(selectedFilters)) {
      return 0
    }

    return Object.values(selectedFilters).filter(Boolean).length
  }, [selectedFilters])

  const selectAddressHandler = useCallback(
    (addressSelected?: SuggestObject) => {
      setAddress(addressSelected)
      setSelectedPoint(undefined)
    },
    [],
  )

  const openFiltersPanelHandler = useCallback(() => {
    setIsFiltersPanelVisible(true)
  }, [])

  const closeFiltersPanelHandler = useCallback(() => {
    setIsFiltersPanelVisible(false)
  }, [])

  const selectPointHandler = useCallback(
    (point?: PickupStation) => {
      setSelectedPoint(prevState => {
        const newSelectedPoint = prevState?.id === point?.id ? undefined : point
        setCenter(getPointCoordinates(point?.position))
        setZoom(18)
        onPointSelect(newSelectedPoint)

        return newSelectedPoint
      })
    },
    [onPointSelect],
  )

  const choosePointHandler = useCallback(
    (point?: PickupStation) => {
      onPointSelect(point)
      onClose()
    },
    [onClose, onPointSelect],
  )

  const selectFilterHandler = useCallback((value?: Record<string, unknown>) => {
    if (isFilterValues(Filter, value)) {
      setSelectedFilters(value)
    }
  }, [])

  return (
    <Modal className={classes.root} opened={isOpen} overlay={{zIndex: 940}}>
      {isPending ? (
        <Loader fullscreenHeight />
      ) : (
        <MapComponent
          address={address}
          center={center}
          onClose={onClose}
          onPointSelect={selectPointHandler}
          pointType={pointType}
          points={filteredPoints}
          selectedPoint={selectedPoint}
          setCenter={setCenter}
          setZoom={setZoom}
          zoom={zoom}
        >
          {isFiltersPanelVisible ? (
            <FiltersSidePanel
              filteredItemsAmount={filteredPoints.length}
              filters={selectedFilters ?? INITIAL_VAlUES}
              onChange={selectFilterHandler}
              onClose={closeFiltersPanelHandler}
              options={filterOptions}
            />
          ) : (
            <PointsSidePanel
              address={address}
              onAddressSelect={selectAddressHandler}
              onCitySelect={onCitySelect}
              onClose={onClose}
              onFilterSelect={selectFilterHandler}
              onOpenFilters={openFiltersPanelHandler}
              onPointChoose={choosePointHandler}
              onPointSelect={selectPointHandler}
              pointType={pointType}
              points={filteredPoints}
              selectedFiltersAmount={selectedFiltersAmountHandler}
              selectedPoint={selectedPoint}
              selectedPointCity={selectedPointCity}
            />
          )}
        </MapComponent>
      )}
    </Modal>
  )
}

const filterOptions = [
  {
    label: i18n('ndd.pickup_points.options.post_payment'),
    name: Filter.POST_PAYMENT,
  },
  {
    label: i18n('ndd.pickup_points.options.only_terminals'),
    name: Filter.POINT_TYPE,
    value: 'terminal',
  },
  {
    label: i18n('ndd.pickup_points.options.only_pickup_points'),
    name: Filter.POINT_TYPE,
    value: 'pickup_point',
  },
]
